/**
 * What the player is told when the socket, the table or the session stops
 * working — as a pure reducer, because the three failures used to be
 * indistinguishable and the difference between them is the whole fix.
 *
 * `useTable` computed `connected` and `sessionInvalid`, nothing read either,
 * and `kicked` only released the action gate. A lost signal, an expired table
 * and a revoked session all looked the same: the screen stopped changing
 * (VERDICT B7). They need three different answers — wait, go home, sign in —
 * so they are three different states here.
 *
 * No React and no socket in this file: the machine is the part worth testing,
 * and it is testable on its own.
 */
import type { SessionInvalidPayload, SessionInvalidReason } from '@cards/shared'

export interface ConnectionState {
  /** The socket itself. `connecting` is only ever the first attempt. */
  readonly link: 'connecting' | 'online' | 'offline'
  /** When the signal went, so the copy can grow from "reconnecting" into
   *  something more honest if it stays gone. null while up. */
  readonly offlineSince: number | null
  /** The server has said this table is over — expired, closed, or gone. It
   *  outranks `link`, because "reconnecting…" would be a lie: reconnecting
   *  will not bring the table back. */
  readonly ended: { readonly reason: string; readonly tableId: string | null } | null
  /** The session itself is dead. The only way out is signing in again, so this
   *  outranks everything. */
  readonly dead: SessionInvalidPayload | null
}

export type ConnectionEvent =
  | { readonly type: 'connected' }
  | { readonly type: 'dropped'; readonly at: number }
  | { readonly type: 'kicked'; readonly reason: string; readonly tableId?: string | null }
  | { readonly type: 'sessionInvalid'; readonly payload: SessionInvalidPayload }
  /** The player has read the "this table is over" message and is going home. */
  | { readonly type: 'acknowledged' }

export const INITIAL_CONNECTION: ConnectionState = {
  link: 'connecting',
  offlineSince: null,
  ended: null,
  dead: null,
}

/** `unauthenticated` / `expired` / `malformed` are verdicts on the *session*:
 *  the handshake token no longer resolves to a user, and no amount of
 *  reconnecting fixes that. `tableNotFound` / `seatNotFound` are verdicts on
 *  the *table* — the session is fine and the player can start another one.
 *
 *  `backendUnavailable` is deliberately not here. It means the store could not
 *  be reached, so nobody knows whether the token is good; treating it as a
 *  dead session logs players out because Redis restarted. */
function killsSession(reason: SessionInvalidReason): boolean {
  return reason === 'unauthenticated' || reason === 'expired' || reason === 'malformed'
}

/** `noTable` is the ordinary answer to the bare `resume` every client sends on
 *  every connect: the player is in the lobby. It is not a failure and nothing
 *  should appear on screen because of it.
 *
 *  This is the one that bit. The server used to answer it with `expired`, the
 *  reducer read that as a dead session, `App` cleared the token — and the
 *  player signed in, connected, resumed and was signed out again, round and
 *  round until the auth rate limiter stopped them. Every account with no live
 *  table, which after a restart is every account. */
function isRoutine(reason: SessionInvalidReason): boolean {
  return reason === 'noTable'
}

export function reduceConnection(
  state: ConnectionState,
  event: ConnectionEvent,
): ConnectionState {
  switch (event.type) {
    case 'connected':
      // Deliberately does not clear `ended` or `dead`: those are things the
      // server said, and a fresh transport is not an answer to either.
      return state.link === 'online' && state.offlineSince === null
        ? state
        : { ...state, link: 'online', offlineSince: null }

    case 'dropped':
      if (state.link === 'offline') return state
      return { ...state, link: 'offline', offlineSince: event.at }

    case 'kicked':
      return { ...state, ended: { reason: event.reason, tableId: event.tableId ?? null } }

    case 'sessionInvalid':
      if (isRoutine(event.payload.reason)) return state
      if (killsSession(event.payload.reason)) return { ...state, dead: event.payload }
      return {
        ...state,
        ended: {
          reason: event.payload.message,
          tableId: event.payload.tableId ?? null,
        },
      }

    case 'acknowledged':
      return state.ended === null ? state : { ...state, ended: null }
  }
}

/** Exactly one of these is on screen, in priority order. `none` is the normal
 *  case and draws nothing at all. */
export type ConnectionScreen = 'none' | 'offline' | 'ended' | 'signIn'

export function connectionScreen(state: ConnectionState): ConnectionScreen {
  if (state.dead) return 'signIn'
  if (state.ended) return 'ended'
  if (state.link === 'offline') return 'offline'
  return 'none'
}

/** Nothing a player does can reach the server, so nothing they tap should look
 *  as though it did. Also false during the first connect, when there is no
 *  table on screen yet anyway. */
export function canAct(state: ConnectionState): boolean {
  return state.link === 'online' && state.ended === null && state.dead === null
}

/**
 * May a lobby message be written into socket.io's own send buffer and delivered
 * when the link comes up?
 *
 * Only before the first connect. There is no table yet and no prior world for a
 * queued message to arrive stale against, and this is what makes "open an
 * invite link, tap Join" work on a slow network. Once a link has existed and
 * gone, queueing is exactly the bug: the tap arrives against a table that moved
 * on while it sat in the buffer (VERDICT B7).
 */
export function canQueue(state: ConnectionState): boolean {
  return state.link === 'connecting' && state.ended === null && state.dead === null
}

/** The reassuring half of the offline copy. It is about the *table*, not the
 *  network, because "your seat is held" is the thing the player actually wants
 *  to know, and it is true: there are no turn clocks. */
export function offlineSentence(state: ConnectionState, now: number): string {
  const seconds = state.offlineSince === null ? 0 : Math.floor((now - state.offlineSince) / 1000)
  if (seconds >= 30) return 'Still trying — your seat is held.'
  return 'Your seat is held. Nothing is lost.'
}
